import { FunnelIcon } from '@heroicons/react/24/outline';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, MinusIcon, UserIcon } from '@heroicons/react/24/solid';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router';
import { ClubResponse } from '../*';
import snippets from '../i18n/lang';
import { classNames } from '../utils/classNames';
import formatNumber from '../utils/formatNumber';
import { Addon } from '../utils/types';
import { addonSummary, filterAddonShares } from '../utils/addonAggregation';
import { ClubStat } from './ClubList';
import { SubHeadline } from './Headline';
import Modal from './Modal';
import { StatsCard } from './StatCard';
import Tooltip from './Tooltip';

const TYPE_FILTER_OPTIONS = [
  { key: 'kostenpflichtig', label: 'Kostenpflichtig', color: 'bg-primary-500' },
  { key: 'fremdabrechnung', label: 'Fremdabrechnung', color: 'bg-primary-400' },
  { key: 'beitragsfrei', label: 'Beitragsfrei', color: 'bg-white border border-gray-300' },
] as const;

const DEFAULT_TYPE_FILTERS = ['kostenpflichtig', 'fremdabrechnung', 'beitragsfrei'];

export const AddonList = ({
  club,
  addons,
  prevAddonsCount,
}: {
  club: ClubResponse;
  addons: Addon[];
  prevAddonsCount?: number;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const params = useParams();
  const [addonList, setAddonList] = useLocalStorage('addonList' + params.clubId, []);
  const [typeFilter, setTypeFilter] = useLocalStorage<string[]>(
    'addonTypeFilter' + params.clubId,
    DEFAULT_TYPE_FILTERS,
  );

  const {
    allActiveMembers,
    allActivated,
    allTerminations,
    allTerminated,
    allInTestphase,
    addonsPerMember,
    addonsPerActivation,
  } = addonSummary(club, addonList, typeFilter);

  useEffect(() => {
    if (prevAddonsCount - addonList.length < 0) {
      setAddonList([]);
    }
  }, [addonList, prevAddonsCount]);

  return (
    <div>
      <div className='flex justify-between items-center mb-3 mt-3'>
        <SubHeadline>{snippets.addonDetail?.headline}</SubHeadline>
        <div
          onClick={() => setModalOpen(true)}
          className='p-1 rounded-full flex items-center gap-2 text-sm px-3 text-gray-700 hover:bg-gray-50 cursor-pointer'
        >
          {addons?.length - addonList.length} / {addons?.length} ADD-ONs
          {typeFilter.length < 3 && (
            <span className='ml-1 text-xs text-primary-500'>({typeFilter.length}/3 Zahlungsarten)</span>
          )}
          <FunnelIcon className='w-4 h-4' />
        </div>
        <Modal isOpen={modalOpen} setIsOpen={setModalOpen} headline={snippets.addonDetail?.showHideHeadline}>
          <div className='flex flex-col gap-1'>
            <div className='flex flex-start'>
              <div
                className='border cursor-pointer flex px-4 p-2 rounded-lg mb-3'
                onClick={() => {
                  if (addonList.length === 0) {
                    return setAddonList(addons.map((a) => a.id));
                  } else {
                    setAddonList([]);
                  }
                }}
              >
                {addonList.length === 0 ? 'Alle ausblenden' : 'Alle einblenden'}
              </div>
            </div>
            {addons?.map((addon, i) => (
              <label key={addon.id} className='flex gap-3 items-center'>
                <input
                  onChange={(e) => {
                    if (addonList.includes(addon.id)) {
                      return setAddonList(addonList.filter((a) => a !== addon.id));
                    }
                    setAddonList([...addonList, addon.id]);
                  }}
                  checked={!addonList.includes(addon.id)}
                  type='checkbox'
                  className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
                />
                {addon.name}
              </label>
            ))}
            <div className='mt-4 border-t pt-4'>
              <div className='text-sm font-medium mb-2'>Zahlungsarten filtern</div>
              {TYPE_FILTER_OPTIONS.map((type) => (
                <label key={type.key} className='flex gap-3 items-center mt-1'>
                  <input
                    type='checkbox'
                    checked={typeFilter.includes(type.key)}
                    onChange={() => {
                      if (typeFilter.includes(type.key)) {
                        setTypeFilter(typeFilter.filter((t) => t !== type.key));
                      } else {
                        setTypeFilter([...typeFilter, type.key]);
                      }
                    }}
                    className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
                  />
                  <div className={`w-2.5 h-2.5 rounded-full ${type.color}`} />
                  {type.label}
                </label>
              ))}
            </div>
          </div>
        </Modal>
      </div>

      <div className='flex flex-col gap-1 mt-3 mb-6'>
        <StatsCard.Plain
          tooltip={`
              ${snippets.customerDetail.staticCards.tooltips.addonSum}
              <br />
              ${snippets.customerDetail.staticCards.tooltips.addonUser}
          `}
          backgroundColor='bg-primary-500 text-white'
        >
          <div className='grid grid-cols-3 w-full'>
            {[
              {
                title: 'Gesamt',
                Icon: ArrowTrendingUpIcon,
                value: allActiveMembers,
              },
              {
                title: 'ADD-ONs / Mitglied',
                Icon: UserIcon,
                value: addonsPerMember.toFixed(2),
              },
            ]?.map((item, i) => (
              <div key={i} className={classNames('w-full overflow-hidden')}>
                <div className='flex justify-between items-center gap-2'>
                  <span className='text-sm text-ellipsis overflow-hidden'>{item.title}</span>
                </div>

                <div className='text-xl mt-1 flex items-center gap-3'>
                  {item.Icon && (
                    //@ts-ignore
                    <item.Icon className='w-5 h-5' />
                  )}
                  {formatNumber(item.value) || <Skeleton height={16} width={100} />}
                </div>
              </div>
            ))}
          </div>
        </StatsCard.Plain>
        <StatsCard.Double
          sign=''
          highlight
          highlightColor='bg-primary-400 text-white'
          items={[
            {
              title: snippets.customerDetail.staticCards.activations,
              sign: '',
              value: allActivated,
            },
            {
              title: 'ADD-ONs / Zugang',
              sign: '',
              value: Number(addonsPerActivation.toFixed(2)) || 0,
            },
          ]}
          title={snippets.customerDetail.staticCards.activations}
          tooltip={`${snippets.customerDetail.staticCards.tooltips.activations}
                      <br /><br />
                      ${snippets.customerDetail.staticCards.tooltips.addonActivations}
                    `}
          value={allActivated}
        />
        <StatsCard.Double
          highlight
          items={[
            {
              title: 'Abgänge',
              sign: '',
              value: allTerminated,
            },
            {
              title: 'davon in Testphase',
              sign: '',
              value: allInTestphase,
            },
            {
              title: 'Kündigungen',
              sign: '',
              value: allTerminations,
            },
          ]}
          tooltip={`
                      ${snippets.customerDetail.staticCards.tooltips.cancelations}
                      <br /><br />
                      ${snippets.customerDetail.staticCards.tooltips.terminations}
                    `}
        />
      </div>
      <div>
        <div className='flex mb-3 gap-4 justify-start flex-wrap'>
          {TYPE_FILTER_OPTIONS.filter((option) => typeFilter.includes(option.key)).map((option) => (
            <div className='flex gap-1.5 text-[10px] items-center' key={option.key}>
              <div className={classNames('w-2.5 h-2.5 rounded-full', option.color)} />
              {option.label}
            </div>
          ))}
        </div>
        {!club?.addons
          ? Array(prevAddonsCount - addonList.length > 0 ? prevAddonsCount - addonList.length || 5 : 5)
              .fill(0)
              .map((_, i) => (
                <div key={i} className='mb-1 rounded-xl bg-gray-100 p-2 px-4'>
                  <div className='flex justify-between text-gray-500 text-sm font-normal mb-2'>
                    <Skeleton width={100} height={16} />
                  </div>

                  <div className='flex items-center justify-between gap-3 mb-3'>
                    <div className='w-6 h-6'>
                      <MinusIcon className='w-9 h-9' />
                    </div>
                    <ClubStat loading signValue={false} name='Aktive Mitglieder' />
                    <ClubStat loading name='Zugänge' />
                    <ClubStat loading name='Kündigungen' />
                  </div>

                  <div className='w-full bg-gray-300 rounded-full text-xs'>
                    <div className='z-30 min-w-min text-ellipsis -mr-2 px-2 py-1 rounded-full overflow-hidden'>
                      {' -'}
                    </div>
                  </div>
                </div>
              ))
          : addons
              ?.filter((a: any) => !addonList.includes(a.id))
              .filter((a) => a.id)
              .sort((a, b) => b.activeMemberships - a.activeMemberships)
              .map((addon) => <AddonCard key={addon.id} addon={addon} typeFilter={typeFilter} />)}
      </div>
    </div>
  );
};

const AddonCard = ({ addon, typeFilter }: { addon: Addon; typeFilter: string[] }) => {
  const change = addon.activeMemberships - (addon.activatedMemberships ?? 0) + (addon.terminationMemberships ?? 0) || 0;

  const beitragsfrei = addon.davonBeitragsfrei ?? 0;
  const fremdabrechnung = addon.davonFremdabrechnung ?? 0;
  const kostenpflichtig = addon.activeMemberships - beitragsfrei - fremdabrechnung;

  const shares = filterAddonShares(addon, typeFilter);
  const filteredAddon = {
    activeMemberships: shares.active,
    activatedMemberships: shares.activated,
    terminatedMemberships: shares.terminated,
    terminationMemberships: shares.termination,
  };

  const TrendingIcon = useMemo(() => {
    const previousValue = addon.activeMemberships - change;
    if (previousValue === 0) {
      return <MinusIcon className='text-gray-500 w-6 h-6 sm:w-8 sm:h-8' />;
    }
    if (previousValue > 0) {
      return <ArrowTrendingUpIcon className='text-primary-500 w-6 h-6 sm:w-8 sm:h-8' />;
    }
    return <ArrowTrendingDownIcon className='text-gray-500 w-6 h-6 sm:w-8 sm:h-8' />;
  }, [change, addon]);

  return (
    <div className='mb-1 rounded-xl bg-gray-100 p-3'>
      <div className='flex justify-between text-gray-500 text-sm font-normal mb-2'>
        <span>{addon.name || 'unknown'}</span>
      </div>

      <div className='flex items-end justify-between gap-2 mb-3'>
        <div className='max-[330px]:hidden self-center'>{TrendingIcon}</div>
        <ClubStat
          large
          signValue={false}
          name='Aktive ADD-ONs'
          compareValue={(100 - (change / (addon.activeMemberships || 1)) * 100).toFixed(1)}
          value={addon.activeMemberships ?? 0}
        />

        {[
          // Comment in Values if needed
          'activatedMemberships',
          // "davonBeitragsfrei",
          // "davonFremdabrechnung",
          'terminatedMemberships',
          'terminationMemberships',
          // 'davonInTestphase',
          // 'davonStandalone',
          // "activeMemberships",
        ].map((key, i, arr) => (
          <ClubStat
            key={key}
            signValue={false}
            light={i === arr.length - 1}
            name={
              {
                activatedMemberships: 'Zugänge',
                davonBeitragsfrei: 'Beitragsfrei',
                davonInTestphase: 'In Testphase',
                davonStandalone: 'Standalone',
                davonFremdabrechnung: 'Fremdabrechnung',
                terminationMemberships: 'Kündigungen',
                terminatedMemberships: 'Abgänge',
              }[key] ?? key
            }
            value={filteredAddon[key] ?? 0}
          />
        ))}
      </div>

      {addon.activeMemberships && (
        <div className='w-full flex text-white rounded-full overflow-hidden text-[10px]'>
          {typeFilter.includes('kostenpflichtig') && kostenpflichtig > 0 && (
            <Tooltip content={kostenpflichtig}>
              <div
                className={`z-30 min-w-min text-ellipsis bg-primary-500 -mr-2 px-2 py-1 rounded-full overflow-hidden`}
                style={{
                  width: `${Math.ceil((kostenpflichtig / addon.activeMemberships) * 100)}%`,
                }}
              >
                {kostenpflichtig}
              </div>
            </Tooltip>
          )}
          {typeFilter.includes('fremdabrechnung') && fremdabrechnung > 0 && (
            <Tooltip content={fremdabrechnung}>
              <div
                className={`z-20 min-w-min text-ellipsis bg-primary-400 pl-4 px-2 -mr-2 py-1 rounded-r-full overflow-hidden`}
                style={{
                  width: `${Math.ceil((fremdabrechnung / addon.activeMemberships) * 100)}%`,
                }}
              >
                {fremdabrechnung}
              </div>
            </Tooltip>
          )}
          {typeFilter.includes('beitragsfrei') && beitragsfrei > 0 && (
            <Tooltip content={beitragsfrei}>
              <div
                className={` bg-white text-primary-500 min-w-min text-ellipsis px-2 py-1 pl-4 rounded-r-full overflow-hidden`}
                style={{
                  width: `${Math.ceil((beitragsfrei / addon.activeMemberships) * 100)}%`,
                }}
              >
                {beitragsfrei}
              </div>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
